import { Box, ThemeProvider, styled } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Drawer } from "./Drawer";
import { Header } from "./Header";
import { Main } from "./Main";
import theme from "../theme/index";
import { useDispatch, useSelector } from "react-redux";
import { DrawerContextProvider } from "../contexts/drawer-context";
import { ListarAtendimentosAbertosAction } from "../store/actions/triagem";
import { loadUsuario, ValidationLogin } from "../store/actions/usuario";
import { io } from "socket.io-client";
import { urlApi } from "../services/url";
import videochamadapng from "../assets/videochamada.png";
import ring from "../assets/music/ring.mp3";
import * as types from "../store/types/triagem";

const InnerContainer = styled(Box)`
  display: flex;
  flex: 1;
  overflow: hidden;
  height: inherit;
`;

export function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { atendimentosAbertos, play_song } = useSelector((state) => state.triagem);
  const { permissoes } = useSelector((state) => state.usuario);

  // Ref sempre atualizado — evita closure stale dentro do socket handler
  const permissoesRef = useRef(permissoes);
  useEffect(() => { permissoesRef.current = permissoes; }, [permissoes]);

  const permisssao       = localStorage.getItem("permissao");
  const id_usuario       = localStorage.getItem("id_usuario");

  const [socketEvent, setSocketEvent]               = useState(false);
  const [playSound, setPlaySound]                   = useState(false);
  const [flashingTitle, setFlashingTitle]           = useState(false);
  const [atendimentoCancelado, setAtendimentoCancelado] = useState(false);
  const [dataSocket, setDataSocket]                 = useState("");
  const [titleInterval, setTitleInterval]           = useState(null);

  // FIX: ref para quantidade — sem isso o socket effect leria valor stale
  const quantidadeRef = useRef(atendimentosAbertos.length);
  useEffect(() => {
    quantidadeRef.current = atendimentosAbertos.length;
  }, [atendimentosAbertos]);

  // FIX: detecta se o usuário está dentro de uma sala de chamada
  // Se sim, não toca ringtone (impede tocar durante chamada ativa)
  const emChamada = location.pathname.includes("/app/home/");

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  useEffect(() => { loadUsuario(); }, []);

  useEffect(() => {
    if (Notification.permission !== "granted") Notification.requestPermission();
  }, []);

  // FIX: socket sem atendimentosAbertos nas deps — evita recriar socket a cada atendimento
  useEffect(() => {
    const socket = io(urlApi);

    socket.on("salaCriada", (data) => {
      // Usa ref — permissoes do Redux pode não estar carregado ainda (closure stale)
      const modalidadeUsuario = permissoesRef.current?.modalidade_id ?? null;
      const deveReceber =
        !modalidadeUsuario ||                         // null = José, recebe tudo
        modalidadeUsuario === data.modalidade_id;     // id certo = recebe a sua

      if (!deveReceber) return;

      setDataSocket(data);
      setSocketEvent(true);
      setPlaySound(true);
      startFlashingTitle();

      if (data.tipo === permisssao) {
        dispatch({ type: types.SET_PLAY_SONG, payload: true });

        if (Notification.permission === "granted") {
          const notification = new Notification("Nova chamada", {
            body: `Uma nova chamada foi solicitada: ${data.participants}`,
            icon: videochamadapng,
          });
          notification.onclick = () => { window.focus(); navigate("/app/home"); };
        }
      }

      setTimeout(() => {
        setSocketEvent(false);
        setPlaySound(false);
        if (quantidadeRef.current === 0) stopFlashingTitle();
      }, 30000);
    });

    socket.on("atendimento-finalizado", (data) => {
      setDataSocket(data);
      setAtendimentoCancelado(true);
      setTimeout(() => {
        setAtendimentoCancelado(false);
        setPlaySound(false);
        if (quantidadeRef.current === 0) stopFlashingTitle();
      }, 1000);
    });

    socket.on("transferenciaChamada", (data) => {
      // Para ATENDIMENTO: só toca se o evento for pra esse usuário
      if (data.atendente_id && String(data.atendente_id) !== String(id_usuario)) return;

      setSocketEvent(true);
      setDataSocket(data);
      setPlaySound(true);
      startFlashingTitle();

      setTimeout(() => {
        setSocketEvent(false);
        setPlaySound(false);
        if (quantidadeRef.current === 0) stopFlashingTitle();
      }, 30000);
    });

    return () => socket.disconnect();
  }, []); // socket criado uma vez — permissoes lidas via ref sempre atualizado

  useEffect(() => {
    ListarAtendimentosAbertosAction();
  }, [socketEvent, atendimentoCancelado, dataSocket]);

  useEffect(() => {
    document.title = `(${atendimentosAbertos.length}) Atendimentos Abertos`;
    if (atendimentosAbertos.length === 0) stopFlashingTitle();
  }, [atendimentosAbertos]);

  // FIX: para o som ao entrar em uma chamada
  useEffect(() => {
    if (emChamada) {
      setPlaySound(false);
      dispatch({ type: types.SET_PLAY_SONG, payload: false });
    }
  }, [emChamada]);

  const startFlashingTitle = () => {
    if (!titleInterval) {
      let flashTitle = false;
      const interval = setInterval(() => {
        document.title = flashTitle
          ? `(${quantidadeRef.current}) Atendimentos Abertos`
          : "⚠️ Novo Atendimento!";
        flashTitle = !flashTitle;
      }, 1000);
      setTitleInterval(interval);
    }
  };

  const stopFlashingTitle = () => {
    if (titleInterval) {
      clearInterval(titleInterval);
      setTitleInterval(null);
      document.title = `(${quantidadeRef.current}) Atendimentos Abertos`;
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      ValidationLogin();
      if (Notification.permission === "granted") {
        const notification = new Notification("Você foi desconectado", {
          body: `Faça login novamente na plataforma`,
          icon: videochamadapng,
        });
        notification.onclick = () => { window.focus(); navigate("/login"); };
      }
    }, 300000);
    return () => clearInterval(intervalId);
  }, []);

  // FIX: não toca se estiver dentro de uma sala de chamada
  function CardSound() {
    if (emChamada) return null;

    return (
      <>
        {dataSocket.tipo === permisssao && play_song ? (
          playSound && (
            <audio autoPlay>
              <source src={ring} type="audio/mp3" />
            </audio>
          )
        ) : dataSocket.tipo === permisssao &&
          play_song &&
          String(id_usuario) === String(dataSocket.atendente_id) ? (
          playSound && (
            <audio autoPlay>
              <source src={ring} type="audio/mp3" />
            </audio>
          )
        ) : null}
      </>
    );
  }

  return (
    <DrawerContextProvider>
      <ThemeProvider theme={theme}>
        <Header />
        <InnerContainer>
          <Drawer />
          <Main>
            <Outlet />
            <CardSound />
          </Main>
        </InnerContainer>
      </ThemeProvider>
    </DrawerContextProvider>
  );
}